// Copyright 2025, University of Maryland, All Rights Reserved
import React, { useState } from 'react';
import { cn } from '@/components/ui';
import { OrganizationDropdown } from './components/OrganizationDropdown';
import { useOrganizations } from './hooks/use-organizations';
import { AppConfigProvider } from './context/AppConfigContext';

// Internal component props (for use within WorkspacesComponent)
export interface OrganizationSelectorInternalProps {
  selectedOrganizationId?: string;
  onOrganizationSelect?: (orgId: string) => void;
  className?: string;
}

// Standalone component props (includes context requirements)
export interface OrganizationSelectorProps extends OrganizationSelectorInternalProps {
  app_id: string;
  debug?: boolean;
}

// Internal component that uses context-based hooks
export const OrganizationSelectorInternal: React.FC<OrganizationSelectorInternalProps> = ({
  selectedOrganizationId,
  onOrganizationSelect,
  className,
}) => {
  // Fetch organizations using the context-based hook
  const { data: organizations = [] } = useOrganizations();
  
  // Internal state for selection if not controlled
  const [internalOrgId, setInternalOrgId] = useState(selectedOrganizationId || '');

  const handleOrganizationSelect = (orgId: string) => {
    if (onOrganizationSelect) {
      onOrganizationSelect(orgId);
    } else {
      setInternalOrgId(orgId);
    }
  };

  const currentOrgId = selectedOrganizationId || internalOrgId;

  return (
    <div data-testid="org-selector" className={cn('organization-selector-component', className)}>
      <OrganizationDropdown
        accessibleOrganizations={organizations}
        selectedOrganizationId={currentOrgId}
        selectOrganization={handleOrganizationSelect}
      />
    </div>
  );
};

// Standalone component that provides its own context
// Note: This component now relies on a parent QueryClientProvider
// The parent app must wrap this component with QueryClientProvider
export const OrganizationSelector: React.FC<OrganizationSelectorProps> = ({
  app_id,
  debug = false,
  ...props
}) => {
  // Note: This selector assumes:
  // 1. The global workspaces API has been initialized via initializeWorkspacesApi()
  // 2. A QueryClientProvider exists in the parent component tree

  return (
    <AppConfigProvider app_id={app_id} debug={debug}>
      <OrganizationSelectorInternal {...props} />
    </AppConfigProvider>
  );
};